import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useTemplateScripts from '../hooks/useTemplateScripts'
import MainHeader from './MainHeader'
import HomeShowcase from './HomeShowcase'
import { ProductCategories, ServicesGrid, WhyChoose, Solutions } from './HomeHighlights'
import FeaturedProducts from './FeaturedProducts'
import ProductQuickView from './ProductQuickView'
import TrustedPartners from './TrustedPartners'
import HomeFooter from './HomeFooter'
import './HomeSections.css'
import CartDrawer from './CartDrawer'
import { useCart } from '../Context/CartContext'
import { getAllProducts } from '../services/productService'
import { CONTACT_SERVICE_INTERESTS, submitContactForm } from '../services/contactService'
import { servicePathFor } from '../constants/servicePages'
import './ContactForm.css'

function Home() {
    useTemplateScripts()
    const navigate = useNavigate()
    const { cartItems, addToCart: addToCartContext, removeFromCart: removeFromCartContext } = useCart()
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [toast, setToast] = useState(null)
    const [showCart, setShowCart] = useState(false)
    const [dealProducts, setDealProducts] = useState([])
    const [productsLoading, setProductsLoading] = useState(true)
    const [productsError, setProductsError] = useState('')
    const [showWhatsApp, setShowWhatsApp] = useState(false)
    
    // Contact form state
    const [contactFormData, setContactFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        subject: '',
        serviceInterest: '',
        message: '',
        privacyAgreed: false
    })
    const [contactFormLoading, setContactFormLoading] = useState(false)
    const [contactFormError, setContactFormError] = useState('')
    const [contactFormSuccess, setContactFormSuccess] = useState('')
    const [serviceSelectOpen, setServiceSelectOpen] = useState(false)

    useEffect(() => {
        if (!serviceSelectOpen) return undefined
        const onPointerDown = (e) => {
            if (!e.target.closest('.fab-service-select')) {
                setServiceSelectOpen(false)
            }
        }
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setServiceSelectOpen(false)
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [serviceSelectOpen])

    // Services data
    const servicesData = [
        {
            id: 'network-infrastructure',
            title: 'Network Infrastructure',
            modalTitle: 'Networks that keep business moving',
            shortDescription: 'Reliable, high-performance networking designed for continuous business operations.',
            image: 'assets/img/service/networking-pro.jpg',
            fullDescription: 'Fabritech designs, installs, and supports enterprise networks—wired and wireless—so your team stays connected with speed, stability, and room to grow.',
            servicePills: [
                { icon: 'clipboard-check', label: 'Assessment' },
                { icon: 'network-wired', label: 'Design & install' },
                { icon: 'wifi', label: 'Wired & wireless' },
                { icon: 'shield-halved', label: 'Secure & monitored' }
            ],
            highlightCards: [
                {
                    image: 'assets/img/service/network-cards/structured-cabling.jpg',
                    title: 'Enterprise switching',
                    description: 'Managed switches and structured cabling built for clean, high-throughput traffic.',
                    position: 'center 20%'
                },
                {
                    image: 'assets/img/service/network-cards/datacenter-rack.jpg',
                    title: 'Always-on infrastructure',
                    description: 'Server-room and rack setups engineered for uptime and easy maintenance.',
                    position: 'center center'
                },
                {
                    image: 'assets/img/service/network-cards/circuit-board.jpg',
                    title: 'Performance hardware',
                    description: 'Quality components and careful configuration for low latency and stable speeds.',
                    position: 'center 40%'
                },
                {
                    image: 'assets/img/service/network-cards/engineer-tech.jpg',
                    title: 'Expert deployment',
                    description: 'From site survey to go-live, our engineers install and tune for your space.',
                    position: 'center 30%'
                },
                {
                    image: 'assets/img/service/network-cards/security-tech.jpg',
                    title: 'Secure by design',
                    description: 'Segmented, monitored networks that protect performance without slowing work.',
                    position: 'center 35%',
                    featured: true,
                    points: ['Firewall', 'VLANs', 'Monitoring', 'Support']
                }
            ],
            installIncludes: [
                'Network assessment and architecture planning',
                'Wired & wireless installation and configuration',
                'Performance tuning and documentation',
                'Proactive monitoring and local support'
            ],
            features: [
                'Network assessment and performance optimization',
                'High-speed internet installation and configuration',
                'Structured network resource management',
                'Continuous monitoring and proactive support',
                'Wired and wireless solutions tailored to your space',
                'Enterprise-grade network security',
                'Scalable architecture that grows with your business',
                'Rapid troubleshooting and issue resolution'
            ],
            benefits: [
                'Consistent connectivity across your workplace',
                'Reduced downtime through proactive monitoring',
                'Improved team productivity and collaboration',
                'Stronger security without sacrificing performance',
                'Efficient use of network resources and bandwidth',
                'Peace of mind with continuous oversight',
                'Infrastructure ready for future growth',
                'A dependable foundation for your digital operations'
            ]
        },
        {
            id: 'digital-security',
            title: 'Digital Security',
            modalTitle: 'Protection for people, places, and data',
            shortDescription: 'Integrated surveillance, access control, and IT security for complete protection.',
            image: 'assets/img/service/security-pro.jpg',
            fullDescription: 'Fabritech designs and installs complete security systems—CCTV, access control, intrusion detection, and IT protection—so your business stays safe and supported after go-live.',
            servicePills: [
                { icon: 'video', label: 'CCTV' },
                { icon: 'lock', label: 'Access control' },
                { icon: 'bell', label: 'Intrusion' },
                { icon: 'shield-halved', label: 'IT security' }
            ],
            highlightCards: [
                {
                    image: 'assets/img/service/security-cards/cctv-dual.jpg',
                    title: 'CCTV surveillance',
                    description: 'IP and professional camera systems with clear coverage for sites of any size.',
                    position: 'center 30%'
                },
                {
                    image: 'assets/img/service/security-cards/smart-lock.jpg',
                    title: 'Access control',
                    description: 'Smart locks, readers, and identity tools that control who enters—and when.',
                    position: 'center 40%'
                },
                {
                    image: 'assets/img/service/security-cards/server-security.jpg',
                    title: 'IT & data security',
                    description: 'Firewalls, endpoint protection, and backups that keep systems and data safe.',
                    position: 'center center'
                },
                {
                    image: 'assets/img/service/security-cards/control-room.jpg',
                    title: 'Monitoring ready',
                    description: 'Surveillance and alerting setups built for real-time visibility and faster response.',
                    position: 'center center'
                },
                {
                    image: 'assets/img/service/security-cards/cctv-triple.jpg',
                    title: 'Complete site protection',
                    description: 'One partner for cameras, access, alarms, and IT security—designed for your risk profile.',
                    position: 'center 35%',
                    featured: true,
                    points: ['CCTV', 'Access', 'Alarms', 'IT security']
                }
            ],
            installIncludes: [
                'Security design tailored to your premises',
                'Professional installation and configuration',
                'System integration across platforms',
                'Training, handover, and ongoing support'
            ],
            features: [
                'End-to-end security system design and delivery',
                'Professional installation and configuration',
                '24/7 monitoring and technical support options',
                'Seamless integration across security platforms',
                'Custom solutions tailored to your risk profile'
            ],
            benefits: [
                'Complete protection for premises, people, and data',
                'Reduced security risks and operational exposure',
                'Improved safety for employees and assets',
                'Alignment with recognized security standards',
                'Confidence through comprehensive coverage'
            ]
        },
        {
            id: 'starlink-installation',
            title: 'Starlink Internet',
            modalTitle: 'High-speed internet that works for you',
            shortDescription: 'Professional Starlink setup for fast, reliable internet in any location.',
            image: 'assets/img/service/starlink-pro.jpg',
            fullDescription: 'Fabritech supplies, mounts, and activates Starlink for homes and businesses—urban or remote—so you get a fast, reliable connection from day one.',
            servicePills: [
                { icon: 'map-location-dot', label: 'Site survey' },
                { icon: 'screwdriver-wrench', label: 'Professional mounting' },
                { icon: 'satellite-dish', label: 'Activation' },
                { icon: 'headset', label: 'Ongoing support' }
            ],
            highlightCards: [
                {
                    image: 'assets/img/service/starlink-cards/rural.jpg',
                    title: 'Rural & remote installs',
                    description: 'High-speed connectivity where fiber and mobile networks fall short.',
                    position: 'center center'
                },
                {
                    image: 'assets/img/service/starlink-cards/roam.jpg',
                    title: 'Stay connected on the go',
                    description: 'Ideal for travel, remote sites, and temporary deployments.',
                    position: 'center 40%'
                },
                {
                    image: 'assets/img/service/starlink-cards/speeds.jpg',
                    title: 'Speeds for real work',
                    description: 'Stream, meet, and work online across multiple devices with confidence.',
                    position: 'center 35%'
                },
                {
                    image: 'assets/img/service/starlink-cards/portable.jpg',
                    title: 'Ultra portable Mini kits',
                    description: 'Compact kits you can deploy in minutes when you need internet fast.',
                    position: 'center 45%'
                },
                {
                    image: 'assets/img/service/starlink-cards/resilient-weather.jpg',
                    title: 'Reliable & resilient',
                    description: 'Stays online through heavy rain, sleet, snow, and harsh winds.',
                    position: 'center 22%',
                    featured: true,
                    points: ['Rain', 'Snow', 'Wind', 'Sleet']
                }
            ],
            installIncludes: [
                'Starlink Standard / Mini kits, routers & cables',
                'Professional mounting and signal optimization',
                'End-to-end setup through activation',
                'Local support from Fabritech'
            ],
            features: [
                'Complete equipment supply — Starlink Standard, Mini, routers, adapters, and cables',
                'Professional mounting, installation, and configuration',
                'Flexible kit options for residential and business needs',
                'End-to-end setup from equipment to connected devices',
                'Signal optimization tuned to your location',
                'Streamlined activation and onboarding'
            ],
            benefits: [
                'Reliable connectivity where traditional networks fall short',
                'High-speed internet for remote and underserved areas',
                'Complete equipment and installation in one package',
                'Faster deployment with expert setup',
                'A single partner for supply, install, and configuration',
                'Dependable performance for everyday work and operations'
            ]
        },
        {
            id: 'software-development',
            title: 'Software Development',
            shortDescription: 'Custom software, web, and mobile solutions aligned to your business goals.',
            image: 'assets/img/home/service-software.jpg',
            fullDescription: 'We develop custom software solutions that turn your operational needs into reliable digital tools. From ERP systems and web platforms to mobile applications, our team delivers well-architected products—from concept and design through launch, integration, and ongoing maintenance.',
            features: [
                'Idea-to-product software development',
                'Custom ERP and business systems',
                'Responsive web applications',
                'Mobile apps for iOS and Android',
                'Scalable database architecture',
                'API design and third-party integrations',
                'Ongoing maintenance and product updates'
            ],
            benefits: [
                'Software built around your actual workflows',
                'Solutions tailored to your business—not generic templates',
                'Faster operations through thoughtful automation',
                'Broader reach with web and mobile access',
                'Technology that scales as you grow',
                'Long-term support from the team that built it',
                'Clear path from concept to measurable results'
            ]
        },
        {
            id: 'canalplus-dstv',
            title: 'Canal+ & DStv Services',
            shortDescription: 'Expert installation, signal support, and convenient subscription renewal.',
            image: 'assets/img/home/service-canal-dstv.jpg',
            fullDescription: 'We provide professional Canal+ and DStv installation, signal troubleshooting, and subscription renewal support. From dish mounting and decoder setup to multi-room configurations and package management, we help you get clear reception and keep your service running smoothly.',
            features: [
                'Professional Canal+ and DStv dish installation',
                'Signal troubleshooting and technical support',
                'Convenient subscription renewal assistance',
                'HD and 4K decoder configuration',
                'Multi-room installation and setup',
                'Signal optimization for clearer reception',
                'Package upgrades, downgrades, and changes'
            ],
            benefits: [
                'Correct installation on the first visit',
                'Faster resolution of weak or unstable signals',
                'Simplified subscription renewals',
                'Improved picture quality through optimized setup',
                'One provider for installation, support, and renewals',
                'Responsive service when issues arise',
                'A smoother entertainment experience overall'
            ]
        },
        {
            id: 'internships-courses',
            title: 'Internships & Short Courses',
            shortDescription: 'Practical academic and professional programs focused on real-world skills.',
            image: 'assets/img/home/service-training.jpg',
            fullDescription: 'We offer academic and professional internship programs built around real project work. Students and early-career professionals gain practical experience under industry mentorship, develop portfolio-ready skills, and receive structured guidance to support their next academic or career step.',
            features: [
                'Academic internships for students seeking credit and experience',
                'Professional internships focused on career development',
                'Hands-on learning through active project work',
                'Real-world assignments with measurable outcomes',
                'Mentorship from experienced industry practitioners',
                'Certificate of completion upon program success',
                'Career guidance and placement support',
                'Flexible schedules to fit academic and work commitments'
            ],
            benefits: [
                'Combine academic credit with practical experience',
                'Build job-ready technical and professional skills',
                'Learn by contributing to real projects',
                'Strengthen your portfolio with tangible work',
                'Receive mentorship from working professionals',
                'Earn recognized proof of completed training',
                'Get support for your next career opportunity',
                'Accelerate growth through applied learning'
            ]
        }
    ]

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 2200)
            return () => clearTimeout(timer)
        }
    }, [toast])

    // Smooth scroll handler for anchor links
    useEffect(() => {
        const handleAnchorClick = (e) => {
            const href = e.target.getAttribute('href')
            if (href && href.startsWith('#')) {
                e.preventDefault()
                const targetId = href.substring(1)
                const targetElement = document.getElementById(targetId)
                if (targetElement) {
                    const headerOffset = 100 // Account for fixed header
                    const elementPosition = targetElement.getBoundingClientRect().top
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    })
                }
            }
        }

        // Add event listener to all anchor links
        const links = document.querySelectorAll('a[href^="#"]')
        links.forEach(link => {
            link.addEventListener('click', handleAnchorClick)
        })

        return () => {
            links.forEach(link => {
                link.removeEventListener('click', handleAnchorClick)
            })
        }
    }, [])

    // Scroll animation observer
    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('scroll-animated')
                    observer.unobserve(entry.target)
                }
            })
        }, observerOptions)

        // Observe all elements with scroll-animate class
        const observeElements = () => {
            const animateElements = document.querySelectorAll('.scroll-animate:not(.scroll-animated)')
            animateElements.forEach(el => observer.observe(el))
        }

        // Initial observation
        observeElements()

        // Re-observe when products are loaded (for dynamically added product cards)
        if (dealProducts.length > 0) {
            // Small delay to ensure DOM is updated
            setTimeout(observeElements, 100)
        }

        return () => {
            const animateElements = document.querySelectorAll('.scroll-animate')
            animateElements.forEach(el => observer.unobserve(el))
        }
    }, [dealProducts])

    // Fetch products from API
    useEffect(() => {
        const fetchProducts = async () => {
            setProductsLoading(true)
            setProductsError('')
            try {
                const data = await getAllProducts()
                console.log('Fetched products data:', data) // Debug log
                
                // Check if data is an array
                if (!Array.isArray(data)) {
                    console.error('Products data is not an array:', data)
                    setProductsError('Invalid products data format')
                    setDealProducts([])
                    return
                }
                
                // Transform API data to match Home component format
                const transformedProducts = data.map((product, index) => {
                    const id = product.id || product._id || `product-${index + 1}`
                    const priceNew = parseFloat(product.priceNew) || parseFloat(product.price) || 0
                    const priceOld = product.priceOld ? parseFloat(product.priceOld) : null
                    
                    // Discount from the shown prices, so the badge always matches them; else the stored value
                    let discount = product.discount ? Math.round(parseFloat(product.discount)) : null
                    if (priceOld && priceOld > priceNew && priceNew > 0) {
                        discount = Math.round(((priceOld - priceNew) / priceOld) * 100)
                    }
                    
                    // Format prices as strings
                    const formattedPriceNew = priceNew > 0 ? `RWF ${priceNew.toLocaleString()}` : 'RWF 0'
                    const formattedPriceOld = priceOld && priceOld > 0 ? `RWF ${priceOld.toLocaleString()}` : null
                    const formattedDiscount = discount && discount > 0 ? `-${discount}%` : null
                    
                    return {
                        id,
                        title: product.title || product.name || 'Untitled Product',
                        badge: product.badge || product.category || 'Featured',
                        image: product.image || '/placeholder-image.png',
                        priceNew: formattedPriceNew,
                        priceOld: formattedPriceOld,
                        discount: formattedDiscount,
                        description: product.description || 'No description available.',
                        // Stock drives availability in the quick view; null when the API has none
                        stock: product.stock ?? null,
                        lowStockThreshold: product.lowStockThreshold ?? null,
                        // Keep original numeric values for cart
                        priceNewNum: priceNew,
                        priceOldNum: priceOld
                    }
                })
                
                console.log('Transformed products:', transformedProducts) // Debug log
                
                // FeaturedProducts filters by category and shows the first few of each
                setDealProducts(transformedProducts)
            } catch (err) {
                setProductsError(err.message || 'Failed to load products')
                console.error('Error fetching products:', err)
                // Set empty array on error
                setDealProducts([])
            } finally {
                setProductsLoading(false)
            }
        }

        fetchProducts()
    }, [])

    const openProduct = (product) => setSelectedProduct(product)
    const closeProduct = () => setSelectedProduct(null)
    // Every service has its own page (/services/:slug)
    const openServiceById = (id) => navigate(servicePathFor(id))
    const exploreService = (service) => openServiceById(service.id)

    // Older links (/?service=<id>) open that service's page
    const [searchParams] = useSearchParams()
    const serviceParam = searchParams.get('service')
    useEffect(() => {
        if (serviceParam) navigate(servicePathFor(serviceParam), { replace: true })
    }, [serviceParam, navigate])

    // Prevent body scroll while the quick view or cart is open
    useEffect(() => {
        if (selectedProduct || showCart) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [selectedProduct, showCart])

    // Show WhatsApp button when scrolling (same logic as back to top)
    useEffect(() => {
        const handleScroll = () => {
            const scroll = window.pageYOffset || document.documentElement.scrollTop
            if (scroll < 500) {
                setShowWhatsApp(false)
            } else {
                setShowWhatsApp(true)
            }
        }
        window.addEventListener('scroll', handleScroll)
        // Check initial scroll position
        handleScroll()
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const addToCart = (product, quantity = 1) => {
        // Convert product format to match CartContext format
        const cartProduct = {
            id: product.id,
            name: product.title,
            title: product.title, // Keep both for compatibility
            price: product.priceNewNum || parseFloat(product.priceNew.replace(/[^\d]/g, '')),
            image: product.image,
            category: product.badge,
            quantity
        }
        addToCartContext(cartProduct, quantity)
        setToast(quantity > 1 ? `${quantity} × ${product.title} added to cart` : `${product.title} added to cart`)
        closeProduct()
    }

    const removeFromCart = (index) => {
        if (cartItems[index]) {
            removeFromCartContext(cartItems[index].id)
            setToast(`Item removed from cart`)
        }
    }

    // Contact form handlers
    const handleContactInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setContactFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
        // Clear errors when user starts typing
        if (contactFormError) {
            setContactFormError('')
        }
    }

    const handleContactSubmit = async (e) => {
        e.preventDefault()
        setContactFormError('')
        setContactFormSuccess('')
        
        // Validate service interest before submission
        if (!contactFormData.serviceInterest || contactFormData.serviceInterest === '') {
            setContactFormError('Please select a service interest')
            setServiceSelectOpen(true)
            const trigger = document.getElementById('contact-service')
            if (trigger) {
                trigger.focus()
                trigger.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
            return
        }
        
        setContactFormLoading(true)

        try {
            const result = await submitContactForm(contactFormData)
            
            // Success
            setContactFormSuccess(result.message || 'Thank you! Your message has been sent successfully. We will get back to you soon!')
            
            // Reset form
            setContactFormData({
                fullName: '',
                email: '',
                phone: '',
                subject: '',
                serviceInterest: '',
                message: '',
                privacyAgreed: false
            })
            setServiceSelectOpen(false)

            // Scroll to top of form to show success message
            const formElement = document.getElementById('contact')
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }

            // Clear success message after 8 seconds (longer for better UX)
            setTimeout(() => {
                setContactFormSuccess('')
            }, 8000)
        } catch (error) {
            setContactFormError(error.message || 'Failed to send message. Please try again.')
        } finally {
            setContactFormLoading(false)
        }
    }

    const toggleCart = () => setShowCart((prev) => !prev)
    
    const handleCheckout = () => {
        setShowCart(false)
        navigate('/cart')
    }
    return (
        <div>
            <style>{`
                html {
                    scroll-behavior: smooth;
                }
                a[href^="#"] {
                    cursor: pointer;
                }

                /* Navbar styling is now unified in App.css */

                /* Scroll Animation Styles */
                .scroll-animate {
                    opacity: 0;
                    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
                }
                /* Ensure product cards are always visible (they're dynamically loaded) */
                .deal-card {
                    opacity: 1 !important;
                    transform: translateY(0) !important;
                    animation: fadeInUp 0.6s ease-out forwards;
                }
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .scroll-animate.fade-up {
                    transform: translateY(40px);
                }

                .scroll-animate.fade-down {
                    transform: translateY(-40px);
                }

                .scroll-animate.fade-left {
                    transform: translateX(-40px);
                }

                .scroll-animate.fade-right {
                    transform: translateX(40px);
                }

                .scroll-animate.scale-up {
                    transform: scale(0.9);
                }

                .scroll-animate.rotate-in {
                    transform: rotate(-5deg) scale(0.95);
                }

                .scroll-animated {
                    opacity: 1 !important;
                    transform: translateY(0) translateX(0) scale(1) rotate(0deg) !important;
                }

                /* Stagger animation delays for child elements */
                .scroll-animate-delay-1 { transition-delay: 0.1s; }
                .scroll-animate-delay-2 { transition-delay: 0.2s; }
                .scroll-animate-delay-3 { transition-delay: 0.3s; }
                .scroll-animate-delay-4 { transition-delay: 0.4s; }
                .scroll-animate-delay-5 { transition-delay: 0.5s; }
                .scroll-animate-delay-6 { transition-delay: 0.6s; }

                /* Contact Form Select Dropdown Styling */
                /* Ensure parent containers don't clip dropdown */
                .tv-contact-us-area,
                .tv-contact-us-area .container,
                .tv-contact-us-area .row,
                .tv-contact-us-area [class*="col-"],
                .form-row,
                .form-group {
                    overflow: visible !important;
                }
                .contact-us-form-wrapper {
                    overflow: visible !important;
                    position: relative;
                }
                .contact-select-wrapper {
                    position: relative;
                    display: block;
                    overflow: visible !important;
                    z-index: 10;
                    margin: 0;
                    padding: 0;
                }
                /* Ensure no duplicate labels or text elements */
                .contact-select-wrapper label:not([for]) {
                    display: none !important;
                }
                /* Ensure select and arrow are properly displayed */
                .contact-select-wrapper .contact-service-select {
                    display: block !important;
                }
                .contact-select-wrapper .select-arrow {
                    display: flex !important;
                }
                .contact-service-select {
                    position: relative;
                    background-image: none !important;
                    background-repeat: no-repeat !important;
                    background-position: right center !important;
                    z-index: 1;
                    min-height: 44px; /* Match input height */
                    line-height: 1.5;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                .contact-service-select option {
                    padding: 12px 15px;
                    background-color: #fff;
                    color: #102a43;
                    font-size: 14px;
                    line-height: 1.5;
                }
                .contact-service-select option[value=""] {
                    color: #94a3b8;
                    font-style: normal;
                }
                /* Ensure dropdown options are visible when select is open */
                .contact-service-select:focus,
                .contact-service-select:active {
                    z-index: 9999 !important;
                    position: relative;
                }
                /* Ensure select is clickable and functional */
                .contact-service-select:not(:disabled) {
                    cursor: pointer;
                }
                .contact-service-select:disabled {
                    cursor: not-allowed;
                    opacity: 0.6;
                    background-color: #f5f5f5;
                }
                /* Match select styling exactly with inputs */
                .contact-service-select:hover:not(:disabled) {
                    border-color: #bbb;
                }
                .contact-service-select:focus {
                    outline: none;
                }
                .contact-service-select:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    background-color: #f5f5f5;
                }
                .contact-service-select:disabled + .select-arrow {
                    opacity: 0.6;
                }
                .select-arrow {
                    transition: transform 0.3s ease, color 0.3s ease;
                    z-index: 10;
                    pointer-events: none;
                }
                
                /* Hide default browser dropdown arrow on all browsers */
                .contact-service-select::-ms-expand {
                    display: none !important;
                }
                .contact-service-select::-webkit-appearance {
                    -webkit-appearance: none !important;
                }
                .contact-service-select::-moz-appearance {
                    -moz-appearance: none !important;
                }
                .contact-service-select {
                    -webkit-appearance: none !important;
                    -moz-appearance: none !important;
                    appearance: none !important;
                    background-image: none !important;
                }
                /* Ensure no duplicate arrows from browser or other sources */
                .contact-service-select option::before,
                .contact-service-select option::after {
                    display: none !important;
                    content: none !important;
                }
                /* Hide any nice-select library styles if present */
                .contact-select-wrapper .nice-select {
                    display: none !important;
                }
                
                /* Ensure arrow is always visible */
                .contact-select-wrapper .select-arrow {
                    display: flex !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                .contact-select-wrapper .select-arrow i {
                    display: inline-block !important;
                    font-size: 11px;
                    line-height: 1;
                }
                /* Hide any duplicate arrows */
                .contact-select-wrapper .select-arrow::before,
                .contact-select-wrapper .select-arrow::after {
                    display: none !important;
                    content: none !important;
                }
                
                /* Mobile responsive for select */
                @media (max-width: 768px) {
                    .contact-service-select {
                        font-size: 16px; /* Prevents zoom on iOS */
                        padding-right: 40px !important;
                    }
                    .select-arrow {
                        right: 15px !important;
                        font-size: 12px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .contact-service-select {
                        padding-right: 40px !important;
                    }
                    .select-arrow {
                        right: 15px !important;
                    }
                }

                /* Contact Success Notification Animations */
                @keyframes slideInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes scaleIn {
                    from {
                        transform: scale(0);
                        opacity: 0;
                    }
                    to {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.05);
                    }
                }

                @keyframes checkmark {
                    0% {
                        transform: scale(0) rotate(-45deg);
                        opacity: 0;
                    }
                    50% {
                        transform: scale(1.2) rotate(-45deg);
                    }
                    100% {
                        transform: scale(1) rotate(0deg);
                        opacity: 1;
                    }
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes progressBar {
                    from {
                        width: 100%;
                    }
                    to {
                        width: 0%;
                    }
                }

                @keyframes float {
                    0% {
                        transform: translate(0, 0) rotate(0deg);
                    }
                    100% {
                        transform: translate(-30px, -30px) rotate(360deg);
                    }
                }

                /* Responsive styles for success notification */
                @media (max-width: 768px) {
                    .contact-success-notification {
                        padding: 20px 20px !important;
                    }
                    .contact-success-notification h4 {
                        font-size: 16px !important;
                    }
                    .contact-success-notification p {
                        font-size: 13px !important;
                    }
                    .contact-success-notification > div > div:first-child {
                        width: 48px !important;
                        height: 48px !important;
                    }
                    .contact-success-notification > div > div:first-child i {
                        font-size: 24px !important;
                    }
                }

                @media (max-width: 480px) {
                    .contact-success-notification {
                        padding: 16px 16px !important;
                        border-radius: 12px !important;
                    }
                    .contact-success-notification > div {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 12px !important;
                    }
                    .contact-success-notification > div > div:first-child {
                        align-self: center;
                    }
                    .contact-success-notification button {
                        position: absolute !important;
                        top: 12px !important;
                        right: 12px !important;
                    }
                    .contact-error-notification {
                        padding: 16px 16px !important;
                        border-radius: 12px !important;
                    }
                    .contact-error-notification > div {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 12px !important;
                    }
                    .contact-error-notification > div > div:first-child {
                        align-self: center;
                    }
                    .contact-error-notification button {
                        position: absolute !important;
                        top: 12px !important;
                        right: 12px !important;
                    }
                }
            `}</style>



            {/* <!-- back-to-top-start  --> */}
            <button
                type="button"
                className="scroll-top scroll-to-target"
                data-target="html"
                aria-label="Back to top"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
                <i className="fa-solid fa-angle-double-up"></i>
            </button>
            {/* <!-- back-to-top-end  --> */}

            {/* <!-- search popup start --> */}
            <div className="search__popup">
                <div className="container">
                    <div className="row">
                        <div className="col-xxl-12">
                            <div className="search__wrapper">
                                <div className="search__top d-flex justify-content-between align-items-center">
                                    <div className="search__logo">
                                        <a href='/'>
                                            <img src="assets/img/logo/F_logo.png" alt="Fabritech" style={{ height: '40px', width: 'auto' }} />
                                        </a>
                                    </div>
                                    <div className="search__close">
                                        <button type="button" className="search__close-btn search-close-btn">
                                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <path d="M17 1L1 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                                                    strokeLinejoin="round" />
                                                <path d="M1 1L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                                                    strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="search__form">
                                    <form action="#">
                                        <div className="search__input">
                                            <input className="search-input-field" type="text" placeholder="Type here to search..." />
                                            <span className="search-focus-border"></span>
                                            <button type="submit">
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M9.55 18.1C14.272 18.1 18.1 14.272 18.1 9.55C18.1 4.82797 14.272 1 9.55 1C4.82797 1 1 4.82797 1 9.55C1 14.272 4.82797 18.1 9.55 18.1Z"
                                                        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                                                        strokeLinejoin="round" />
                                                    <path d="M19.0002 19.0002L17.2002 17.2002" stroke="currentColor" strokeWidth="1.5"
                                                        strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* <!-- search popup end --> */}


            <MainHeader cartCount={cartItems.length} onCartClick={toggleCart} onOpenService={openServiceById} />

            <main>
                <HomeShowcase />
                <ProductCategories onOpenService={openServiceById} />
                <ServicesGrid services={servicesData} onLearnMore={exploreService} />


                {/* <!-- product-area-start --> */}
                <style>{`
                    @keyframes productSlideInUp {
                        from {
                            opacity: 0;
                            transform: translateY(40px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes productImageZoom {
                        from {
                            transform: scale(1);
                        }
                        to {
                            transform: scale(1.1);
                        }
                    }
                    @keyframes overlayFadeIn {
                        from {
                            opacity: 0;
                            backdrop-filter: blur(0px);
                        }
                        to {
                            opacity: 1;
                            backdrop-filter: blur(4px);
                        }
                    }
                    @keyframes pulseGlow {
                        0%, 100% {
                            box-shadow: 0 0 0 0 rgba(52, 144, 206, 0.4);
                        }
                        50% {
                            box-shadow: 0 0 0 10px rgba(52, 144, 206, 0);
                        }
                    }
                    @keyframes sizeButtonGlow {
                        0%, 100% {
                            box-shadow: 0 0 0 0 rgba(52, 144, 206, 0.3);
                        }
                        50% {
                            box-shadow: 0 0 0 8px rgba(52, 144, 206, 0);
                        }
                    }
                    @keyframes cartButtonHover {
                        0% {
                            transform: scale(1);
                        }
                        50% {
                            transform: scale(1.08);
                        }
                        100% {
                            transform: scale(1);
                        }
                    }
                    .product-card {
                        background: #fff;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                        border: 1px solid #f0f0f0;
                        animation: productSlideInUp 0.8s ease-out forwards;
                    }
                    .product-card:nth-child(1) { animation-delay: 0.1s; }
                    .product-card:nth-child(2) { animation-delay: 0.2s; }
                    .product-card:nth-child(3) { animation-delay: 0.3s; }
                    .product-card:nth-child(4) { animation-delay: 0.4s; }
                    .product-card:nth-child(5) { animation-delay: 0.5s; }
                    .product-card:nth-child(6) { animation-delay: 0.6s; }
                    .product-card:hover {
                        box-shadow: 0 12px 24px rgba(52, 144, 206, 0.15);
                        transform: translateY(-8px);
                        border-color: #3490ce;
                    }
                    .product-image-container {
                        position: relative;
                        width: 100%;
                        height: 280px;
                        overflow: hidden;
                        background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
                    }
                    .product-image-container img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .product-card:hover .product-image-container img {
                        animation: productImageZoom 0.6s ease-out forwards;
                    }
                    .product-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(26, 26, 26, 0.75);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        opacity: 0;
                        animation: overlayFadeIn 0.4s ease-out;
                        transition: opacity 0.4s ease;
                    }
                    .product-card:hover .product-overlay {
                        opacity: 1;
                    }
                    .product-view-btn {
                        width: 56px;
                        height: 56px;
                        border-radius: 50%;
                        background: #3490ce;
                        color: #fff;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 24px;
                        transition: all 0.3s ease;
                        box-shadow: 0 4px 12px rgba(52, 144, 206, 0.3);
                        text-decoration: none;
                        animation: pulseGlow 2s infinite;
                    }
                    .product-view-btn:hover {
                        background: #3490ce;
                        transform: scale(1.15);
                        box-shadow: 0 6px 16px rgba(52, 144, 206, 0.4);
                        animation: none;
                    }

                    /* Product quick-view modal */
                    .product-modal-overlay {
                        position: fixed;
                        inset: 0;
                        background: rgba(0, 0, 0, 0.55);
                        backdrop-filter: blur(2px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 9999;
                        padding: 20px;
                    }
                    .product-modal {
                        width: min(900px, 100%);
                        background: #fff;
                        border-radius: 18px;
                        overflow: hidden;
                        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
                        display: grid;
                        grid-template-columns: 1.1fr 1fr;
                        gap: 0;
                    }
                    @media (max-width: 900px) {
                        .product-modal {
                            grid-template-columns: 1fr;
                        }
                    }
                    .product-modal-image {
                        background: #f6f8fb;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 30px;
                    }
                    .product-modal-image img {
                        width: 100%;
                        height: auto;
                        object-fit: contain;
                    }
                    .product-modal-body {
                        padding: 26px 28px;
                        display: flex;
                        flex-direction: column;
                        gap: 14px;
                    }
                    .product-modal-chip {
                        align-self: flex-start;
                        padding: 8px 12px;
                        border-radius: 999px;
                        background: linear-gradient(135deg, #3490ce 0%, #2366b1 100%);
                        color: #fff;
                        font-weight: 800;
                        font-size: 12px;
                        letter-spacing: 0.3px;
                        text-transform: uppercase;
                    }
                    .product-modal-title {
                        font-size: 24px;
                        font-weight: 900;
                        color: #0f172a;
                        margin: 0;
                        line-height: 1.2;
                    }
                    .product-modal-desc {
                        font-size: 15px;
                        color: #475467;
                        line-height: 1.6;
                        margin: 0;
                    }
                    .product-modal-meta {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        color: #111827;
                        font-weight: 800;
                    }
                    .product-modal-meta .old {
                        color: #9ca3af;
                        text-decoration: line-through;
                        font-weight: 600;
                    }
                    .product-modal-meta .discount {
                        color: #dc2626;
                        font-weight: 800;
                    }
                    .product-modal-rating {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        color: #475467;
                        font-size: 14px;
                    }
                    .product-modal-stars {
                        color: #f59e0b;
                        display: inline-flex;
                        gap: 2px;
                    }
                    .product-modal-actions {
                        display: flex;
                        gap: 12px;
                        margin-top: 10px;
                        flex-wrap: wrap;
                    }
                    .product-modal-close {
                        background: #f3f4f6;
                        color: #111827;
                        border: 1px solid #e5e7eb;
                        border-radius: 12px;
                        padding: 12px 16px;
                        font-weight: 700;
                        cursor: pointer;
                        transition: all 0.2s ease;
                    }
                    .product-modal-close:hover {
                        background: #e5e7eb;
                    }

                    .product-body {
                        padding: 28px 24px;
                    }
                    .product-title {
                        font-size: 18px;
                        font-weight: 700;
                        color: #102a43;
                        margin-bottom: 12px;
                        transition: color 0.3s ease;
                    }
                    .product-title a {
                        color: #102a43;
                        text-decoration: none;
                        transition: color 0.3s ease;
                    }
                    .product-title a:hover {
                        color: #3490ce;
                    }
                    .product-description {
                        font-size: 14px;
                        color: #64748b;
                        line-height: 1.7;
                        margin-bottom: 20px;
                        min-height: 70px;
                    }
                    .product-footer-info {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding-top: 16px;
                        border-top: 1px solid #f0f0f0;
                        gap: 12px;
                    }
                    .product-price-tag {
                        font-size: 13px;
                        font-weight: 600;
                        color: #3490ce;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .product-inquiry-btn {
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        background: #f8f9fa;
                        color: #3490ce;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 16px;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        text-decoration: none;
                        border: 2px solid transparent;
                    }
                    .product-inquiry-btn:hover {
                        background: #3490ce;
                        color: #fff;
                        transform: translateX(4px) scale(1.1);
                        box-shadow: 0 4px 12px rgba(52, 144, 206, 0.3);
                    }

                    /* Professional product cards — equal height, fill grid cells */
                    .deal-card {
                        width: 100%;
                        height: 100%;
                        margin: 0;
                        display: flex;
                        flex-direction: column;
                        background: #ffffff;
                        border-radius: 16px;
                        box-shadow: 0 6px 20px rgba(16, 42, 67, 0.06);
                        overflow: hidden;
                        border: 1px solid rgba(16, 42, 67, 0.08);
                        transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
                        position: relative;
                        cursor: pointer;
                    }
                    .deal-card:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 14px 32px rgba(35, 102, 177, 0.12);
                        border-color: rgba(52, 144, 206, 0.22);
                    }
                    .deal-image {
                        position: relative;
                        width: 100%;
                        height: 180px;
                        flex-shrink: 0;
                        overflow: hidden;
                        background: #f3f6f9;
                    }
                    .deal-image img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                        object-position: center;
                        display: block;
                        padding: 14px;
                        transition: transform 0.4s ease;
                    }
                    .deal-card:hover .deal-image img {
                        transform: scale(1.03);
                    }
                    .deal-wishlist-btn {
                        position: absolute;
                        top: 10px;
                        right: 10px;
                        width: 36px;
                        height: 36px;
                        border-radius: 50%;
                        background: #ffffff;
                        border: 1px solid rgba(16, 42, 67, 0.08);
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 3px 10px rgba(16, 42, 67, 0.1);
                        cursor: pointer;
                        z-index: 2;
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        padding: 0;
                    }
                    .deal-wishlist-btn:hover {
                        transform: scale(1.05);
                        box-shadow: 0 5px 14px rgba(16, 42, 67, 0.14);
                    }
                    .deal-wishlist-btn i {
                        color: #64748b !important;
                        font-size: 14px !important;
                        transition: color 0.2s ease, transform 0.2s ease;
                    }
                    .deal-wishlist-btn:hover i {
                        color: #2366b1 !important;
                    }
                    .deal-wishlist-btn.active i {
                        color: #dc2626 !important;
                    }
                    .toast {
                        position: fixed;
                        top: 24px;
                        right: 24px;
                        background: #0f172a;
                        color: #fff;
                        padding: 12px 16px;
                        border-radius: 10px;
                        box-shadow: 0 12px 24px rgba(0,0,0,0.2);
                        z-index: 10000;
                        font-weight: 700;
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                    }
                    .cart-fab {
                        position: fixed;
                        bottom: 28px;
                        right: 24px;
                        background: #2366b1;
                        color: #fff;
                        border: none;
                        border-radius: 14px;
                        padding: 12px 16px;
                        font-weight: 800;
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        box-shadow: 0 12px 26px rgba(35, 102, 177, 0.35);
                        cursor: pointer;
                        z-index: 10001;
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                    }
                    .cart-fab:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 16px 30px rgba(35, 102, 177, 0.45);
                    }
                    .cart-badge {
                        min-width: 22px;
                        height: 22px;
                        border-radius: 999px;
                        background: #fff;
                        color: #2366b1;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 12px;
                        font-weight: 900;
                        padding: 0 6px;
                    }
                    .cart-panel {
                        width: min(520px, 100%);
                        max-height: 80vh;
                        background: #fff;
                        border-radius: 16px;
                        box-shadow: 0 24px 60px rgba(0,0,0,0.18);
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                    }
                    .cart-panel-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 18px 20px;
                        border-bottom: 1px solid #e5e7eb;
                        background: #f8fafc;
                    }
                    .cart-panel-title {
                        margin: 0;
                        font-size: 18px;
                        font-weight: 800;
                        color: #0f172a;
                    }
                    .cart-panel-body {
                        padding: 16px 20px;
                        overflow-y: auto;
                        display: grid;
                        gap: 12px;
                    }
                    .cart-line {
                        display: flex;
                        gap: 12px;
                        align-items: center;
                        padding: 12px;
                        border: 1px solid #f1f5f9;
                        border-radius: 12px;
                        background: #fff;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.04);
                    }
                    .cart-line img {
                        width: 64px;
                        height: 64px;
                        object-fit: cover;
                        border-radius: 10px;
                        background: #f6f8fb;
                    }
                    .cart-line h5 {
                        margin: 0 0 6px 0;
                        font-size: 15px;
                        color: #0f172a;
                        font-weight: 800;
                    }
                    .cart-line .price {
                        font-weight: 800;
                        color: #102a43;
                        font-size: 14px;
                    }
                    .cart-line .badge {
                        font-size: 11px;
                        font-weight: 800;
                        color: #2366b1;
                        background: rgba(52, 144, 206, 0.12);
                        padding: 4px 8px;
                        border-radius: 999px;
                        border: 1px solid rgba(52, 144, 206, 0.2);
                    }
                    .cart-remove {
                        margin-left: auto;
                        background: #fee2e2;
                        color: #b91c1c;
                        border: 1px solid #fecdd3;
                        border-radius: 10px;
                        padding: 8px 10px;
                        font-weight: 700;
                        cursor: pointer;
                        transition: background 0.2s ease, transform 0.2s ease;
                    }
                    .cart-remove:hover {
                        background: #fecdd3;
                        transform: translateY(-1px);
                    }
                    .deal-body {
                        display: flex;
                        flex-direction: column;
                        flex: 1 1 auto;
                        padding: 14px;
                        gap: 8px;
                        min-height: 0;
                    }
                    .deal-title {
                        font-size: 15px;
                        color: #102a43;
                        font-weight: 700;
                        margin: 0;
                        line-height: 1.35;
                        min-height: calc(1.35em * 2);
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }
                    .deal-rating {
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 5px;
                        font-size: 12px;
                        color: #64748b;
                        margin: 0;
                        padding: 0;
                        border: none;
                    }
                    .deal-stars {
                        color: #f59e0b;
                        display: inline-flex;
                        align-items: center;
                        gap: 1px;
                        font-size: 11px;
                    }
                    .deal-rating-value {
                        color: #64748b;
                        font-weight: 600;
                    }
                    .deal-reviews {
                        color: #94a3b8;
                        font-weight: 500;
                    }
                    .deal-pricing {
                        display: flex;
                        align-items: baseline;
                        flex-wrap: wrap;
                        gap: 6px;
                        margin: 0;
                        min-height: 28px;
                    }
                    .deal-price-new {
                        font-size: 17px;
                        font-weight: 700;
                        color: #2366b1;
                        letter-spacing: -0.02em;
                    }
                    .deal-price-old {
                        font-size: 12px;
                        color: #94a3b8;
                        text-decoration: line-through;
                    }
                    .deal-discount {
                        font-size: 11px;
                        font-weight: 700;
                        color: #b91c1c;
                        background: rgba(185, 28, 28, 0.08);
                        border: 1px solid rgba(185, 28, 28, 0.12);
                        border-radius: 999px;
                        padding: 2px 7px;
                    }
                    .deal-add-to-cart {
                        width: 100%;
                        margin-top: auto;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        min-height: 40px;
                        padding: 10px 14px;
                        border: none;
                        border-radius: 10px;
                        background: #3490ce;
                        color: #ffffff;
                        font-size: 13px;
                        font-weight: 700;
                        cursor: pointer;
                        transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
                    }
                    .deal-add-to-cart:hover {
                        background: #2366b1;
                        box-shadow: 0 8px 18px rgba(35, 102, 177, 0.25);
                    }
                    .deal-add-to-cart:active {
                        transform: translateY(1px);
                    }
                    .deal-add-to-cart i {
                        font-size: 13px;
                    }
                    .fab-products-grid {
                        display: grid;
                        grid-template-columns: repeat(3, minmax(0, 1fr));
                        gap: 22px;
                        margin-bottom: 40px;
                        align-items: stretch;
                    }
                    @media (max-width: 900px) {
                        .fab-products-grid {
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                            gap: 16px;
                        }
                        .deal-image {
                            height: 160px;
                        }
                    }
                    @media (max-width: 560px) {
                        .fab-products-grid {
                            grid-template-columns: 1fr;
                            gap: 14px;
                        }
                        .deal-image {
                            height: 180px;
                        }
                    }

                    /* Section headers for product blocks */
                    .section-header {
                        display: flex;
                        align-items: flex-end;
                        justify-content: space-between;
                        gap: 28px;
                        flex-wrap: wrap;
                        position: relative;
                    }
                    .section-header.centered {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        justify-content: center;
                    }
                    .section-header.highlight::after {
                        content: '';
                        position: absolute;
                        left: -40px;
                        top: -60px;
                        width: 200px;
                        height: 200px;
                        background: radial-gradient(circle, rgba(52, 144, 206, 0.14) 0%, rgba(52, 144, 206, 0) 70%);
                        z-index: 0;
                        pointer-events: none;
                    }
                    .section-chip {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 7px 14px;
                        background: rgba(52, 144, 206, 0.08);
                        color: #2366b1;
                        border: 1px solid rgba(52, 144, 206, 0.18);
                        border-radius: 999px;
                        font-size: 12px;
                        font-weight: 700;
                        letter-spacing: 0.12em;
                        text-transform: uppercase;
                        position: relative;
                        z-index: 1;
                    }
                    .section-chip::before {
                        content: '';
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: #3490ce;
                    }
                    .section-title {
                        font-size: 36px;
                        font-weight: 700;
                        color: #102a43;
                        margin: 14px 0 12px 0;
                        line-height: 1.2;
                        letter-spacing: -0.4px;
                        position: relative;
                        z-index: 1;
                    }
                    .section-title span {
                        color: #2366b1;
                    }
                    .section-subtitle {
                        font-size: 15.5px;
                        color: #5a6b73;
                        line-height: 1.75;
                        max-width: 560px;
                        margin: 0;
                        z-index: 1;
                    }
                    .section-actions {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        z-index: 1;
                        padding-bottom: 4px;
                    }
                    .primary-btn {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        padding: 13px 22px;
                        background: linear-gradient(135deg, #3490ce 0%, #2366b1 100%);
                        color: #fff;
                        border-radius: 12px;
                        font-weight: 600;
                        font-size: 14px;
                        text-decoration: none;
                        border: 1px solid transparent;
                        box-shadow: 0 8px 20px rgba(35, 102, 177, 0.22);
                        transition: all 0.25s ease;
                        cursor: pointer;
                    }
                    .primary-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 12px 24px rgba(35, 102, 177, 0.28);
                        color: #fff;
                    }
                    .primary-btn:active {
                        transform: translateY(0);
                    }
                    .ghost-btn {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        padding: 12px 18px;
                        background: #ffffff;
                        color: #2366b1;
                        border-radius: 12px;
                        font-weight: 600;
                        font-size: 14px;
                        text-decoration: none;
                        border: 1px solid rgba(52, 144, 206, 0.28);
                        transition: all 0.25s ease;
                    }
                    .ghost-btn:hover {
                        background: rgba(52, 144, 206, 0.06);
                        color: #102a43;
                        transform: translateY(-1px);
                    }

                    @media (max-width: 768px) {
                        .section-title {
                            font-size: 28px;
                        }
                        .section-header {
                            align-items: flex-start;
                        }
                        .section-actions {
                            width: 100%;
                            flex-wrap: wrap;
                        }
                    }

                    /* Fabritech Featured Product Card Styles */
                    .hydro-sync-card {
                        position: relative;
                        background: linear-gradient(145deg, #ffffff 0%, #f7f9fc 100%);
                        border-radius: 20px;
                        overflow: hidden;
                        box-shadow: 0 14px 40px rgba(16, 24, 40, 0.12);
                        transition: all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
                        border: 1px solid rgba(52, 144, 206, 0.08);
                        max-width: 430px;
                        margin: 0 auto;
                        isolation: isolate;
                    }
                    .hydro-sync-card::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        border-radius: 20px;
                        padding: 1px;
                        background: linear-gradient(135deg, rgba(52, 144, 206, 0.4), rgba(255, 255, 255, 0));
                        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                        -webkit-mask-composite: xor;
                        mask-composite: exclude;
                        opacity: 0.6;
                    }
                    .hydro-sync-card:hover {
                        box-shadow: 0 24px 60px rgba(16, 24, 40, 0.16);
                        transform: translateY(-10px) scale(1.01);
                        border-color: rgba(52, 144, 206, 0.25);
                    }
                    .hydro-image-wrapper {
                        position: relative;
                        width: 100%;
                        height: 340px;
                        background: radial-gradient(circle at 20% 20%, rgba(52, 144, 206, 0.12), transparent 40%), #f5f7fb;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        overflow: hidden;
                    }
                    .hydro-image-wrapper img {
                        width: 80%;
                        height: auto;
                        object-fit: contain;
                        transition: transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
                    }
                    .hydro-sync-card:hover .hydro-image-wrapper img {
                        transform: scale(1.12) rotate(2deg);
                    }
                    .hydro-body {
                        padding: 28px;
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    .hydro-meta {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                    }
                    .hydro-chip {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px 12px;
                        border-radius: 999px;
                        background: rgba(52, 144, 206, 0.12);
                        color: #2366b1;
                        font-weight: 700;
                        font-size: 12px;
                        letter-spacing: 0.5px;
                    }
                    .hydro-rating {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 10px;
                        background: #fff;
                        border: 1px solid #eef2f7;
                        border-radius: 10px;
                        font-weight: 700;
                        color: #0f172a;
                        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
                    }
                    .hydro-rating i {
                        color: #f59e0b;
                    }
                    .hydro-product-title {
                        font-size: 22px;
                        font-weight: 800;
                        color: #0f172a;
                        margin: 0;
                        letter-spacing: -0.3px;
                    }
                    .hydro-product-subtitle {
                        font-size: 13px;
                        color: #3490ce;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        margin: 0;
                    }
                    .hydro-description {
                        font-size: 14px;
                        color: #4b5563;
                        line-height: 1.7;
                        margin: 0;
                    }
                    .hydro-feature-list {
                        list-style: none;
                        padding: 0;
                        margin: 0;
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 8px 12px;
                    }
                    .hydro-feature-list li {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 13px;
                        color: #475467;
                    }
                    .hydro-feature-list li i {
                        color: #3490ce;
                        font-size: 12px;
                    }
                    .hydro-size-selector {
                        margin-bottom: 28px;
                    }
                    .hydro-size-label {
                        display: block;
                        font-size: 12px;
                        font-weight: 700;
                        color: #102a43;
                        text-transform: uppercase;
                        letter-spacing: 0.8px;
                        margin-bottom: 12px;
                    }
                    .hydro-size-options {
                        display: flex;
                        gap: 10px;
                        flex-wrap: wrap;
                    }
                    .hydro-size-btn {
                        padding: 10px 16px;
                        border: 2px solid #ddd;
                        background: #fff;
                        color: #64748b;
                        border-radius: 24px;
                        font-size: 13px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        flex: 1 1 calc(50% - 5px);
                        text-align: center;
                        min-width: 70px;
                    }
                    .hydro-size-btn:hover {
                        border-color: #2366b1;
                        color: #2366b1;
                    }
                    .hydro-size-btn.active {
                        background: #2366b1;
                        color: #fff;
                        border-color: #2366b1;
                        animation: sizeButtonGlow 2s infinite;
                    }
                    .hydro-footer {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding-top: 18px;
                        border-top: 1px solid #eef2f7;
                        gap: 12px;
                    }
                    .hydro-price {
                        font-size: 28px;
                        font-weight: 800;
                        color: #102a43;
                        letter-spacing: -0.5px;
                    }
                    .hydro-price-currency {
                        font-size: 14px;
                        font-weight: 700;
                        color: #3490ce;
                    }
                    .hydro-add-to-cart {
                        flex: 1;
                        padding: 15px 22px;
                        background: #2366b1;
                        color: #fff;
                        border: 1px solid #2366b1;
                        border-radius: 14px;
                        font-size: 15px;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 12px;
                        box-shadow: 0 12px 26px rgba(35, 102, 177, 0.28);
                        letter-spacing: 0.4px;
                        position: relative;
                        overflow: hidden;
                        text-transform: uppercase;
                    }
                    .hydro-add-to-cart::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.22) 50%, rgba(255, 255, 255, 0) 100%);
                        transition: left 0.55s ease;
                    }
                    .hydro-add-to-cart::after {
                        content: '';
                        position: absolute;
                        inset: 2px;
                        border-radius: 12px;
                        border: 1px solid rgba(255, 255, 255, 0.18);
                        opacity: 0.8;
                        pointer-events: none;
                    }
                    .hydro-add-to-cart:hover::before {
                        left: 100%;
                    }
                    .hydro-add-to-cart:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 16px 32px rgba(35, 102, 177, 0.4);
                        background: #1b528f;
                        border-color: #1b528f;
                    }
                    .hydro-add-to-cart:active {
                        transform: translateY(-1px);
                        box-shadow: 0 8px 16px rgba(35, 102, 177, 0.3);
                    }
                    .hydro-cart-icon {
                        font-size: 18px;
                        transition: transform 0.35s ease, color 0.35s ease;
                        color: #fff;
                    }
                    .hydro-add-to-cart:hover .hydro-cart-icon {
                        transform: scale(1.2) translateX(3px);
                    }
                `}</style>
                <FeaturedProducts
                    products={dealProducts}
                    loading={productsLoading}
                    error={productsError}
                    onOpen={openProduct}
                    onAdded={(product) => setToast(`${product.name} added to cart`)}
                />
                {/* <!-- product-area-end --> */}

                <WhyChoose />
                <Solutions />
                <TrustedPartners />

                <style>{`
                    @keyframes slideInLeft {
                        from {
                            opacity: 0;
                            transform: translateX(-50px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    @keyframes slideInRight {
                        from {
                            opacity: 0;
                            transform: translateX(50px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes float {
                        0%, 100% {
                            transform: translateY(0px);
                        }
                        50% {
                            transform: translateY(-10px);
                        }
                    }
                    @keyframes pulse {
                        0%, 100% {
                            box-shadow: 0 2px 10px rgba(52, 144, 206, 0);
                        }
                        50% {
                            box-shadow: 0 2px 20px rgba(52, 144, 206, 0.3);
                        }
                    }
                    .contact-left-animated {
                        animation: slideInLeft 0.8s ease-out forwards;
                    }
                    .contact-form-animated {
                        animation: slideInRight 0.8s ease-out 0.2s forwards;
                        opacity: 0;
                    }
                    .contact-info-animated {
                        animation: fadeInUp 0.6s ease-out forwards;
                        opacity: 0;
                    }
                    .contact-info-animated:nth-child(1) { animation-delay: 0.3s; }
                    .contact-info-animated:nth-child(2) { animation-delay: 0.5s; }
                    .contact-info-animated:nth-child(3) { animation-delay: 0.7s; }
                    .contact-info-item:hover {
                        animation: pulse 1.5s ease-in-out infinite;
                        transform: translateY(-5px);
                        transition: transform 0.3s ease;
                    }
                    .form-input-animated {
                        position: relative;
                        overflow: hidden;
                    }
                    /* Removed decorative blue line animation */
                    .btn-submit-animated {
                        position: relative;
                        overflow: hidden;
                    }
                    .btn-submit-animated::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: rgba(255, 255, 255, 0.2);
                        transition: left 0.5s ease;
                    }
                    .btn-submit-animated:hover::before {
                        left: 100%;
                    }
                    .btn-submit-animated:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(52, 144, 206, 0.4) !important;
                    }
                    .contact-link-hover {
                        display: inline-block;
                        transition: transform 0.3s ease;
                    }
                    .contact-link-hover:hover {
                        transform: translateX(5px);
                    }

                    /* Float visuals only — size/position/bob from App.css stack */
                    .scroll-top {
                        color: #ffffff !important;
                        cursor: pointer !important;
                        background: linear-gradient(135deg, #3490ce 0%, #2366b1 100%) !important;
                        border: none !important;
                        box-shadow: 0 4px 14px rgba(52, 144, 206, 0.35) !important;
                        transition: box-shadow 0.25s ease, opacity 0.25s ease !important;
                        opacity: 0;
                        pointer-events: none;
                    }
                    .scroll-top.open {
                        opacity: 1 !important;
                        pointer-events: auto;
                    }
                    .scroll-top.open:hover {
                        box-shadow: 0 8px 18px rgba(52, 144, 206, 0.45) !important;
                    }
                    .scroll-top i {
                        color: #ffffff !important;
                    }
                    .whatsapp-float {
                        background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 4px 16px rgba(37, 211, 102, 0.35);
                        transition: box-shadow 0.25s ease, opacity 0.3s ease;
                        text-decoration: none;
                        opacity: 0;
                        pointer-events: none;
                    }
                    .whatsapp-float.show {
                        opacity: 1;
                        pointer-events: auto;
                    }
                    .whatsapp-float.show:hover {
                        box-shadow: 0 8px 22px rgba(37, 211, 102, 0.45);
                    }
                    .whatsapp-float i {
                        color: #ffffff;
                        transition: transform 0.2s ease;
                    }
                    .whatsapp-float:hover i {
                        transform: scale(1.06);
                    }
                    .whatsapp-tooltip {
                        position: absolute;
                        right: calc(100% + 10px);
                        top: 50%;
                        transform: translateY(-50%);
                        background: #0f172a;
                        color: #fff;
                        padding: 6px 10px;
                        border-radius: 8px;
                        font-size: 12px;
                        font-weight: 650;
                        white-space: nowrap;
                        opacity: 0;
                        pointer-events: none;
                        transition: opacity 0.18s ease;
                        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.2);
                    }
                    .whatsapp-tooltip::after {
                        content: '';
                        position: absolute;
                        left: 100%;
                        top: 50%;
                        transform: translateY(-50%);
                        border: 6px solid transparent;
                        border-left-color: #0f172a;
                    }
                    .whatsapp-float:hover .whatsapp-tooltip {
                        opacity: 1;
                    }
                    @media (max-width: 560px) {
                        .whatsapp-tooltip {
                            display: none;
                        }
                    }
`}</style>
                <div id="contact" className="tv-contact-us-area pt-60 pb-60" style={{ backgroundColor: '#f8f9fa', overflow: 'visible', position: 'relative' }}>
                    <div className="container">
                        <div className="row align-items-start">
                            <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 mb-40 mb-lg-0">
                                <div className="fab-contact-visual">
                                    <img src="/assets/img/home/contact-consultation.jpg" alt="" className="fab-contact-visual__photo" />
                                    <div className="fab-contact-visual__panel">
                                        <span className="fab-chip">Contact Us</span>
                                        <h2 className="fab-title">Let's Talk About <span className="fab-gradient-text">Your Project</span></h2>
                                        <p className="fab-intro">Whether you need a quotation, technical support or want to discuss a project, our team is ready to assist you.</p>
                                        <ul className="fab-contact-visual__list">
                                            <li>
                                                <span aria-hidden="true"><i className="fa-solid fa-phone" /></span>
                                                <a href="tel:+250788601280">+250 788 601 280</a>
                                            </li>
                                            <li>
                                                <span aria-hidden="true"><i className="fa-solid fa-envelope" /></span>
                                                <a href="mailto:info@fabritech.rw">info@fabritech.rw</a>
                                            </li>
                                            <li>
                                                <span aria-hidden="true"><i className="fa-solid fa-location-dot" /></span>
                                                <a href="https://maps.google.com/?q=YYUSSA+Plaza+Kisimenti+Remera+Kigali" target="_blank" rel="noreferrer">YYUSSA Plaza, Kisimenti, Remera, Kigali</a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 p-relative">
                                <div className="fab-contact-form scroll-animate fade-right">
                                    <p className="fab-contact-form__eyebrow">
                                        <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                                        Get in touch
                                    </p>
                                    <h3 className="fab-contact-form__title">Send us a Message</h3>
                                    <p className="fab-contact-form__lead">
                                        Tell us what you need and we will respond as soon as possible.
                                    </p>

                                    {contactFormSuccess && (
                                        <div className="fab-contact-alert fab-contact-alert--success" role="status">
                                            <div className="fab-contact-alert__icon">
                                                <i className="fa-solid fa-check" aria-hidden="true"></i>
                                            </div>
                                            <div className="fab-contact-alert__body">
                                                <strong>Message sent</strong>
                                                <p>{contactFormSuccess}</p>
                                            </div>
                                            <button
                                                type="button"
                                                className="fab-contact-alert__close"
                                                onClick={() => setContactFormSuccess('')}
                                                aria-label="Dismiss success message"
                                            >
                                                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    )}

                                    {contactFormError && (
                                        <div className="fab-contact-alert fab-contact-alert--error" role="alert">
                                            <div className="fab-contact-alert__icon">
                                                <i className="fa-solid fa-exclamation" aria-hidden="true"></i>
                                            </div>
                                            <div className="fab-contact-alert__body">
                                                <strong>Something went wrong</strong>
                                                <p>{contactFormError}</p>
                                            </div>
                                            <button
                                                type="button"
                                                className="fab-contact-alert__close"
                                                onClick={() => setContactFormError('')}
                                                aria-label="Dismiss error message"
                                            >
                                                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    )}

                                    <form onSubmit={handleContactSubmit} noValidate={false}>
                                        <div className="fab-contact-grid">
                                            <div className="fab-contact-field fab-contact-field--full">
                                                <label htmlFor="contact-fullName">Full name <span>*</span></label>
                                                <input
                                                    id="contact-fullName"
                                                    type="text"
                                                    name="fullName"
                                                    placeholder="Enter your full name"
                                                    required
                                                    value={contactFormData.fullName}
                                                    onChange={handleContactInputChange}
                                                    disabled={contactFormLoading}
                                                    autoComplete="name"
                                                />
                                            </div>

                                            <div className="fab-contact-field">
                                                <label htmlFor="contact-email">Email <span>*</span></label>
                                                <input
                                                    id="contact-email"
                                                    type="email"
                                                    name="email"
                                                    placeholder="you@example.com"
                                                    required
                                                    value={contactFormData.email}
                                                    onChange={handleContactInputChange}
                                                    disabled={contactFormLoading}
                                                    autoComplete="email"
                                                />
                                            </div>

                                            <div className="fab-contact-field">
                                                <label htmlFor="contact-phone">Phone</label>
                                                <input
                                                    id="contact-phone"
                                                    type="tel"
                                                    name="phone"
                                                    placeholder="+250 ..."
                                                    value={contactFormData.phone}
                                                    onChange={handleContactInputChange}
                                                    disabled={contactFormLoading}
                                                    autoComplete="tel"
                                                />
                                            </div>

                                            <div className="fab-contact-field">
                                                <label htmlFor="contact-subject">Subject <span>*</span></label>
                                                <input
                                                    id="contact-subject"
                                                    type="text"
                                                    name="subject"
                                                    placeholder="What is this about?"
                                                    required
                                                    value={contactFormData.subject}
                                                    onChange={handleContactInputChange}
                                                    disabled={contactFormLoading}
                                                />
                                            </div>

                                            <div className="fab-contact-field">
                                                <label id="contact-service-label">Service interest <span>*</span></label>
                                                <div className={`fab-service-select ${serviceSelectOpen ? 'is-open' : ''} ${contactFormData.serviceInterest ? 'has-value' : ''}`}>
                                                    <button
                                                        type="button"
                                                        id="contact-service"
                                                        className="fab-service-select__trigger"
                                                        aria-haspopup="listbox"
                                                        aria-expanded={serviceSelectOpen}
                                                        aria-labelledby="contact-service-label"
                                                        disabled={contactFormLoading}
                                                        onClick={() => setServiceSelectOpen((open) => !open)}
                                                    >
                                                        <span className="fab-service-select__value">
                                                            {contactFormData.serviceInterest || 'Select a service'}
                                                        </span>
                                                        <i className="fa-solid fa-chevron-down fab-service-select__chevron" aria-hidden="true"></i>
                                                    </button>
                                                    {serviceSelectOpen && (
                                                        <ul className="fab-service-select__menu" role="listbox" aria-labelledby="contact-service-label">
                                                            {CONTACT_SERVICE_INTERESTS.map((option) => (
                                                                <li key={option} role="option" aria-selected={contactFormData.serviceInterest === option}>
                                                                    <button
                                                                        type="button"
                                                                        className={`fab-service-select__option ${contactFormData.serviceInterest === option ? 'is-selected' : ''}`}
                                                                        onClick={() => {
                                                                            setContactFormData((prev) => ({ ...prev, serviceInterest: option }))
                                                                            setContactFormError('')
                                                                            setServiceSelectOpen(false)
                                                                        }}
                                                                    >
                                                                        {option}
                                                                        {contactFormData.serviceInterest === option && (
                                                                            <i className="fa-solid fa-check" aria-hidden="true"></i>
                                                                        )}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="fab-contact-field fab-contact-field--full">
                                                <label htmlFor="contact-message">Message <span>*</span></label>
                                                <textarea
                                                    id="contact-message"
                                                    name="message"
                                                    placeholder="Share a few details about your request..."
                                                    rows="3"
                                                    required
                                                    value={contactFormData.message}
                                                    onChange={handleContactInputChange}
                                                    disabled={contactFormLoading}
                                                ></textarea>
                                            </div>
                                        </div>

                                        <div className="fab-contact-check">
                                            <input
                                                type="checkbox"
                                                id="agree"
                                                name="privacyAgreed"
                                                required
                                                checked={contactFormData.privacyAgreed}
                                                onChange={handleContactInputChange}
                                                disabled={contactFormLoading}
                                            />
                                            <label htmlFor="agree">
                                                I agree to the privacy policy and terms &amp; conditions
                                            </label>
                                        </div>

                                        <button
                                            type="submit"
                                            className="fab-contact-submit"
                                            disabled={contactFormLoading}
                                        >
                                            {contactFormLoading ? (
                                                <>
                                                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    Send Message
                                                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* <!-- Contact-Us-area-end --> */}
            </main>

            {selectedProduct && (
                <ProductQuickView product={selectedProduct} onClose={closeProduct} onAddToCart={addToCart} />
            )}

            <CartDrawer
                open={showCart}
                onClose={() => setShowCart(false)}
                onToast={(message) => setToast(message)}
            />

            {toast && (
                <div className="toast">
                    <i className="fa-solid fa-circle-check" style={{ color: '#22c55e' }}></i>
                    <span>{toast}</span>
                </div>
            )}

            <HomeFooter />

            {/* WhatsApp Floating Button */}
            <a 
                href="https://wa.me/250788601280?text=Hello%20Fabritech!%20I%20would%20like%20to%20know%20more%20about%20your%20services." 
                target="_blank" 
                rel="noopener noreferrer" 
                className={`whatsapp-float ${showWhatsApp ? 'show' : ''}`}
                aria-label="Chat with us on WhatsApp"
            >
                <i className="fa-brands fa-whatsapp"></i>
                <span className="whatsapp-tooltip">Chat with us on WhatsApp</span>
            </a>
        </div>
    )
}
export default Home;
